import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, LogOut, MapPin, Upload, Trash2, Plus } from "lucide-react";
import { fileUrl } from "../components/Gallery";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const login = () => {
  const redirectUrl = window.location.origin + "/admin";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function AdminPage() {
  const [authState, setAuthState] = useState(null); // null=checking, obj=user, false=guest
  const [trips, setTrips] = useState([]);
  const [form, setForm] = useState({ city: "", country: "", story: "" });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const checkAuth = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setAuthState(data);
    } catch {
      setAuthState(false);
    }
  };

  const loadTrips = async () => {
    try {
      const { data } = await axios.get(`${API}/trips`);
      setTrips(data);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) return;
    checkAuth();
    loadTrips();
  }, []);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    setAuthState(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.city || !form.country) {
      toast.error("Please add at least a city and country.");
      return;
    }
    setSaving(true);
    const fd = new FormData();
    fd.append("city", form.city);
    fd.append("country", form.country);
    fd.append("story", form.story);
    if (file) fd.append("file", file);
    try {
      await axios.post(`${API}/trips`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${form.city} added to your map.`);
      setForm({ city: "", country: "", story: "" });
      setFile(null);
      const el = document.getElementById("trip-file");
      if (el) el.value = "";
      loadTrips();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save the trip.");
    } finally {
      setSaving(false);
    }
  };

  const removeTrip = async (id) => {
    try {
      await axios.delete(`${API}/trips/${id}`, { withCredentials: true });
      setTrips((t) => t.filter((x) => x.id !== id));
      toast.success("Trip removed from the map.");
    } catch {
      toast.error("Couldn't remove the trip.");
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-[#2C223A] focus:border-[#D6B56D] text-[#EBE4D8] placeholder:text-[#A59DAB]/60 py-3 outline-none transition-colors";

  // checking
  if (authState === null) {
    return (
      <div className="relative z-10 min-h-screen flex items-center justify-center text-[#A59DAB]">
        <p className="font-heading italic text-2xl">Checking access…</p>
      </div>
    );
  }

  // not logged in
  if (authState === false) {
    return (
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6 text-center">
        <img src="/ovidiu-logo.png" alt="Ovidiu" className="h-16 w-auto object-contain mb-10" />
        <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D6B56D] mb-5">
          Private area
        </p>
        <h1 className="font-heading font-light text-5xl text-[#EBE4D8] mb-8">
          Admin <span className="italic text-[#D6B56D]">access</span>
        </h1>
        <button
          data-testid="google-login-button"
          onClick={login}
          className="px-9 py-3.5 rounded-full bg-[#D6B56D] text-[#06040A] text-sm tracking-widest uppercase font-medium hover:bg-[#F0D494] transition-all duration-300"
        >
          Sign in with Google
        </button>
        <Link to="/" className="mt-8 text-sm text-[#A59DAB] hover:text-[#D6B56D] inline-flex items-center gap-2">
          <ArrowLeft size={15} /> Back home
        </Link>
      </div>
    );
  }

  // logged in but not admin
  if (!authState.is_admin) {
    return (
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6 text-center">
        <h1 className="font-heading font-light text-4xl text-[#EBE4D8] mb-4">Not authorized</h1>
        <p className="text-[#A59DAB] mb-8">
          {authState.email} doesn't have admin access to this site.
        </p>
        <button onClick={logout} className="px-7 py-3 rounded-full border border-[#D6B56D]/50 text-[#EBE4D8] text-sm uppercase tracking-widest hover:bg-[#D6B56D]/10">
          Sign out
        </button>
      </div>
    );
  }

  // admin dashboard
  return (
    <div data-testid="admin-page" className="relative z-10 min-h-screen">
      <header className="sticky top-0 z-50 bg-[#06040A]/80 backdrop-blur-xl border-b border-[#2C223A]">
        <div className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/"><img src="/ovidiu-logo.png" alt="Ovidiu" className="h-12 md:h-14 w-auto object-contain" /></Link>
          <div className="flex items-center gap-5">
            <Link to="/" className="text-sm text-[#A59DAB] hover:text-[#D6B56D] inline-flex items-center gap-2">
              <ArrowLeft size={15} /> Site
            </Link>
            <button
              data-testid="logout-button"
              onClick={logout}
              className="text-sm text-[#A59DAB] hover:text-[#D6B56D] inline-flex items-center gap-2"
            >
              <LogOut size={15} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-14 md:py-20">
        <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D6B56D] mb-4">
          Welcome back, {authState.name?.split(" ")[0] || "traveler"}
        </p>
        <h1 className="font-heading font-light text-5xl md:text-6xl text-[#EBE4D8] mb-12">
          Manage <span className="italic text-[#D6B56D]">your journeys</span>
        </h1>

        {/* add form */}
        <form
          data-testid="trip-form"
          onSubmit={submit}
          className="backdrop-blur-xl bg-[#120E1A]/60 border border-[#D6B56D]/20 rounded-3xl p-6 md:p-8 mb-16"
        >
          <h2 className="font-heading text-2xl text-[#EBE4D8] mb-6 flex items-center gap-2">
            <Plus size={20} className="text-[#D6B56D]" /> Add a new trip
          </h2>
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#A59DAB]">City</label>
              <input data-testid="trip-city-input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="e.g. Lisbon" className={inputCls} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#A59DAB]">Country</label>
              <input data-testid="trip-country-input" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="e.g. Portugal" className={inputCls} />
            </div>
          </div>
          <div className="mb-6">
            <label className="text-xs uppercase tracking-[0.2em] text-[#A59DAB]">Story</label>
            <textarea data-testid="trip-story-input" value={form.story} onChange={(e) => setForm({ ...form, story: e.target.value })} rows={3} placeholder="What did this place mean to you?" className={`${inputCls} resize-none`} />
          </div>
          <div className="flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
            <input
              id="trip-file"
              data-testid="trip-file-input"
              type="file"
              accept="image/*"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block text-sm text-[#A59DAB] file:mr-4 file:py-2.5 file:px-5 file:rounded-full file:border-0 file:text-sm file:bg-[#2C223A] file:text-[#EBE4D8] hover:file:bg-[#3a2e4d] file:cursor-pointer cursor-pointer"
            />
            <button
              data-testid="trip-submit-button"
              type="submit"
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full bg-[#D6B56D] text-[#06040A] text-sm tracking-widest uppercase font-medium hover:bg-[#F0D494] transition-all disabled:opacity-60"
            >
              <Upload size={16} /> {saving ? "Saving..." : "Add to map"}
            </button>
          </div>
          <p className="text-xs text-[#A59DAB]/70 mt-4">
            Coordinates are found automatically from the city & country. The pin appears on your globe instantly.
          </p>
        </form>

        {/* trips list */}
        <h2 className="font-heading text-2xl text-[#EBE4D8] mb-6">
          Your map · {trips.length} places
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-trips-list">
          {trips.map((t, i) => (
            <motion.div
              key={t.id}
              data-testid={`admin-trip-${i}`}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: (i % 6) * 0.04 }}
              className="relative rounded-2xl overflow-hidden border border-[#2C223A] bg-[#120E1A]/50"
            >
              {t.image_path ? (
                <img src={fileUrl(t.image_path)} alt={t.city} className="w-full h-36 object-cover" />
              ) : (
                <div className="w-full h-36 flex items-center justify-center bg-[#0c0a14]">
                  <MapPin size={26} className="text-[#D6B56D]/50" />
                </div>
              )}
              <div className="p-4">
                <p className="font-heading text-xl text-[#EBE4D8]">
                  {t.is_home ? "★ " : ""}{t.city}
                </p>
                <p className="text-xs uppercase tracking-[0.2em] text-[#D6B56D]">{t.country}</p>
                {t.story && <p className="text-sm text-[#A59DAB] mt-2 line-clamp-2">{t.story}</p>}
              </div>
              {!t.is_home && (
                <button
                  data-testid={`admin-trip-delete-${t.id}`}
                  onClick={() => removeTrip(t.id)}
                  aria-label="Delete trip"
                  className="absolute top-3 right-3 w-9 h-9 rounded-full bg-[#06040A]/70 backdrop-blur text-[#EBE4D8] flex items-center justify-center hover:bg-red-500/80 transition-all"
                >
                  <Trash2 size={15} />
                </button>
              )}
            </motion.div>
          ))}
        </div>
      </main>
    </div>
  );
}
